"use client";

import { c as _c } from "react/compiler-runtime";
import { RefObject, useSyncExternalStore } from "react";
import { useScroll, useSpring, useTransform } from "framer-motion";

// Desktop is an external store, read during render rather than copied into
// state from an effect — the same pattern the Services section already uses for
// the viewport.
const subscribeToViewport = (onStoreChange: () => void) => {
  window.addEventListener("resize", onStoreChange);
  return () => window.removeEventListener("resize", onStoreChange);
};
// Tailwind's lg breakpoint. The rail only exists above it.
const getSnapshot = () => window.innerWidth >= 1024;
// No window on the server. `false` is also the snapshot React uses for the
// hydrating render, so the markup matches and nothing animates before mount.
const getServerSnapshot = () => false;

// Breathing space between the vertical heading and the deck, added to the
// rail's open width, in px.
const RAIL_GAP = 44;

/**
 * Swaps the Case Study heading from a horizontal block above the deck for a
 * vertical one down the left, scrubbed by scroll.
 *
 * Not a rotation: the top heading fades away and a second, vertical heading
 * writes itself in on the left — the deck sliding right to give it room. Only
 * opacity, clip and a single width are animated. The width is a real layout
 * value, but ScrollStack runs at scaleStep 0 here, so its scroll output drives
 * nothing visual and the reflow cannot feed back as jitter.
 */
export const useHeadingRail = (triggerRef, textWidth, textHeight) => {
  const $ = _c(20);
  const isDesktop = useSyncExternalStore(subscribeToViewport, getSnapshot, getServerSnapshot);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["start start", "end start"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== triggerRef) {
    t1 = {
      target: triggerRef,
      offset: t0
    };
    $[1] = triggerRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    scrollYProgress
  } = useScroll(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 120,
      damping: 26,
      restDelta: 0.001
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const progress = useSpring(scrollYProgress, t2);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 0.35];
    t4 = [1, 0];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const topOpacity = useTransform(progress, t3, t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [0, 0.35];
    t6 = [0, -24];
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  const topY = useTransform(progress, t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [0, 0.85];
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = textHeight ? textHeight + RAIL_GAP : 0;
  let t9;
  if ($[9] !== t8) {
    t9 = [0, t8];
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const railWidth = useTransform(progress, t7, t9);
  let t10;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [0.28, 0.98];
    t11 = [0, 1];
    $[11] = t10;
    $[12] = t11;
  } else {
    t10 = $[11];
    t11 = $[12];
  }
  const railReveal = useTransform(progress, t10, t11);
  const railY = textWidth;
  let t12;
  if ($[13] !== isDesktop || $[14] !== railReveal || $[15] !== railWidth || $[16] !== railY || $[17] !== topOpacity || $[18] !== topY) {
    t12 = {
      isDesktop,
      topOpacity,
      topY,
      railWidth,
      railReveal,
      railY
    };
    $[13] = isDesktop;
    $[14] = railReveal;
    $[15] = railWidth;
    $[16] = railY;
    $[17] = topOpacity;
    $[18] = topY;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};